import React, { useState, useEffect } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import SparkApi from "../../api/SparkApi";
import { toast } from "react-toastify";

const ProductImageGallery = ({ productId, primaryFileId, primaryTitle }) => {
  const [images, setImages]             = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    if (!productId) return;
    const fetchImages = async () => {
      try {
        const result = await SparkApi.fetchFiles(productId);
        if (result) setImages(result);
        else if (primaryFileId && primaryTitle)
          setImages([{ id: primaryFileId, title: primaryTitle, parentid: productId }]);
      } catch (err) {
        console.error("Error fetching product images:", err);
      }
    };
    fetchImages();
  }, [productId, primaryFileId]);

  const buildSrc = (img) => {
    const ext = img.title?.split(".").pop() || "jpeg";
    return `/ibs_ibirds/${img.parentid}/${img.id}.${ext}`;
  };

  const handleDelete = async (e, imgId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this image?")) return;
    try {
      await SparkApi.deleteFile(imgId);
      setImages((prev) => prev.filter((i) => i.id !== imgId));
      if (lightboxIndex !== null) setLightboxIndex(null);
      toast.success("Image deleted");
    } catch {
      toast.error("Failed to delete image");
    }
  };

  const openLightbox  = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage     = () => setLightboxIndex((i) => (i - 1 + images.length) % images.length);
  const nextImage     = () => setLightboxIndex((i) => (i + 1) % images.length);

  if (!images.length) return null;

  return (
    <>
      <Row className="g-2 mt-1">
        {images.map((img, idx) => (
          <Col xs={6} sm={4} md={3} key={img.id}>
            <div
              style={{
                position: "relative",
                border: "2px solid #dee2e6",
                borderRadius: "8px",
                overflow: "hidden",
                aspectRatio: "1",
                background: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={() => openLightbox(idx)}
            >
              <img
                src={buildSrc(img)}
                alt={img.title || `Image ${idx + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentElement.innerHTML =
                    '<span style="color:#aaa;font-size:12px;padding:8px">No Preview</span>';
                }}
              />
              {/* Delete button */}
              <button
                onClick={(e) => handleDelete(e, img.id)}
                title="Delete image"
                style={{
                  position: "absolute",
                  top: 4, right: 4,
                  width: 22, height: 22,
                  borderRadius: "50%",
                  background: "rgba(220,53,69,0.85)",
                  border: "none",
                  color: "#fff",
                  fontSize: "12px",
                  lineHeight: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 2,
                }}
              >
                &times;
              </button>
            </div>
          </Col>
        ))}
      </Row>

      {lightboxIndex !== null && (
        <Modal show onHide={closeLightbox} centered size="lg" style={{ backdropFilter: "blur(4px)" }}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "0.95rem", color: "#555" }}>
              {images[lightboxIndex]?.title || `Image ${lightboxIndex + 1}`}
              &nbsp;
              <span style={{ fontWeight: 400, color: "#999" }}>
                ({lightboxIndex + 1} / {images.length})
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            className="text-center p-2"
            style={{ background: "#111", minHeight: "400px", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <img
              src={buildSrc(images[lightboxIndex])}
              alt={images[lightboxIndex]?.title}
              style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: "4px" }}
            />
          </Modal.Body>
          <Modal.Footer className="justify-content-between" style={{ background: "#111", borderTop: "1px solid #333" }}>
            <Button variant="outline-light" size="sm" onClick={prevImage} disabled={images.length < 2}>← Prev</Button>
            <div className="d-flex gap-2">
              {images.map((img, idx) => (
                <div
                  key={img.id}
                  onClick={() => setLightboxIndex(idx)}
                  style={{
                    width: 48, height: 48,
                    cursor: "pointer",
                    border: idx === lightboxIndex ? "2px solid #fff" : "2px solid transparent",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <img src={buildSrc(img)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              ))}
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(e) => handleDelete(e, images[lightboxIndex].id)}
              >
                Delete
              </Button>
              <Button variant="outline-light" size="sm" onClick={nextImage} disabled={images.length < 2}>Next →</Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default ProductImageGallery;